import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  HANDOUT_COLUMNS,
  HANDOUT_COLUMN_LABELS,
  HANDOUT_STATUS_LABELS,
  HANDOUT_STATUSES,
  type HandoutContextDto,
  type HandoutDrawerDto,
  type HandoutEventDto,
  type HandoutOrderListDto,
  type HandoutOverviewDto,
  type HandoutPresetDto,
  type HandoutResultDto,
  type HandoutScope,
  type HandoutStatus,
  type HandoutUserDto,
  type HandoutZoneDto,
} from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, BarChart, ErrorBox, Modal, PageTitle, Spinner, StatTile } from '../components/ui';

type Tab = 'overview' | 'orders' | 'log' | 'reports' | 'zones' | 'users';
const statusTone: Record<HandoutStatus, 'amber' | 'green' | 'blue' | 'zinc'> = { pending: 'amber', handed_out: 'green', approval_required: 'blue', cancelled: 'zinc' };
const actionLabel: Record<string, string> = { handout: 'Udleveret', recall: 'Taget tilbage', handout_refused: 'Udlevering afvist', recall_refused: 'Tilbagetagning afvist' };
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

export interface OrderFilter {
  status: HandoutStatus[];
  zipFrom: string;
  zipTo: string;
  departments: string[] | null;
  products: string[];
  q: string;
}
const emptyFilter: OrderFilter = { status: [], zipFrom: '', zipTo: '', departments: null, products: [], q: '' };
const filterParams = (shopId: string, f: OrderFilter) => {
  const p = new URLSearchParams({ shopId });
  if (f.status.length) p.set('status', f.status.join(','));
  if (f.zipFrom) p.set('zipFrom', f.zipFrom);
  if (f.zipTo) p.set('zipTo', f.zipTo);
  if (f.departments) p.set('departments', f.departments.join('|'));
  if (f.products.length) p.set('products', f.products.join('|'));
  if (f.q.trim()) p.set('q', f.q.trim());
  return p;
};

/** HO-02 to HO-05: QR2's editor and admin as one page; the scanner lives on the handheld at /m/udlevering. */
export function HandoutPage() {
  const { can } = useAuth();
  const ctx = useQuery({ queryKey: ['handout-context'], queryFn: () => get<HandoutContextDto>('/handout/context') });
  const [shopId, setShopId] = useState('');
  const [tab, setTab] = useState<Tab>('overview');
  const [filter, setFilter] = useState<OrderFilter>(emptyFilter);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (ctx.data && !shopId) setShopId(ctx.data.shopId ?? '');
  }, [ctx.data, shopId]);
  if (!ctx.data) return ctx.error ? <ErrorBox error={ctx.error} /> : <Spinner />;
  const tabs: [Tab, string, boolean][] = [['overview', 'Overblik', true], ['orders', 'Ordrer', true], ['log', 'Log', true], ['reports', 'Rapporter', can('handout.reports')], ['zones', 'Zoner', can('handout.manage')], ['users', 'Brugere', can('handout.manage')]];
  const showOrders = (f: Partial<OrderFilter>) => { setFilter({ ...emptyFilter, ...f }); setTab('orders'); };
  return (
    <div className="space-y-4">
      <PageTitle
        title="Udlevering"
        subtitle="Gaver udleveret til medarbejderne via QR-koden på kvitteringen. Scanneren er på håndterminalen under /m/udlevering."
        actions={
          <select className="input w-72" value={shopId} onChange={(e) => setShopId(e.target.value)}>
            {ctx.data.shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        }
      />
      {ctx.data.scope && <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">Du ser kun ordrer i dit område: {ctx.data.scope.kind === 'department' ? ctx.data.scope.departments.join(', ') : ctx.data.scope.kind === 'zip' ? `postnr. ${ctx.data.scope.from}-${ctx.data.scope.to}` : ''}</div>}
      <div className="flex gap-1 border-b border-line">
        {tabs.filter((t) => t[2]).map(([k, label]) => (
          <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => { setTab(k); setError(null); }}>{label}</button>
        ))}
      </div>
      <ErrorBox error={error} />
      {!shopId ? <p className="text-sm text-muted">Ingen gaveshops at vise.</p> : (
        <>
          {tab === 'overview' && <OverviewTab shopId={shopId} onShow={showOrders} />}
          {tab === 'orders' && <OrdersTab shopId={shopId} filter={filter} setFilter={setFilter} zones={ctx.data.zones.filter((z) => z.shopId === shopId)} onError={setError} />}
          {tab === 'log' && <LogTab shopId={shopId} />}
          {tab === 'reports' && <ReportsTab shopId={shopId} onError={setError} />}
          {tab === 'zones' && <ZonesTab shopId={shopId} onError={setError} />}
          {tab === 'users' && <UsersTab shops={ctx.data.shops} shopId={shopId} onError={setError} />}
        </>
      )}
    </div>
  );
}

function OverviewTab({ shopId, onShow }: { shopId: string; onShow: (f: Partial<OrderFilter>) => void }) {
  const o = useQuery({ queryKey: ['handout-overview', shopId], queryFn: () => get<HandoutOverviewDto>(`/handout/overview?shopId=${shopId}`), refetchInterval: 60_000 });
  if (!o.data) return o.error ? <ErrorBox error={o.error} /> : <Spinner />;
  const d = o.data;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <StatTile label="Gaver i alt" value={d.total} />
        <StatTile label="Udleveret" value={d.handedOut} />
        <StatTile label="Mangler" value={d.missing} />
        <StatTile label="Kræver godkendelse" value={d.approval} />
        <StatTile label="Udleveret i dag" value={d.today} delta={d.yesterday ? Math.round(((d.today - d.yesterday) / d.yesterday) * 100) : null} deltaLabel={`i går ${d.yesterday} · taget tilbage i dag ${d.recallsToday}`} tone="up-good" />
      </div>
      <div className="card">
        <div className="label">Seneste 14 dage</div>
        <BarChart labels={d.days.map((x) => x.date.slice(5))} series={[{ name: 'Udleveret', values: d.days.map((x) => x.handouts), color: '#16a34a' }, { name: 'Taget tilbage', values: d.days.map((x) => x.recalls), color: '#dc2626' }]} height={180} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-0">
          <table className="table text-sm">
            <thead><tr><th>Afdeling</th><th className="text-right">Gaver</th><th className="text-right">Mangler</th><th></th></tr></thead>
            <tbody>
              {d.byDepartment.map((r) => (
                <tr key={r.department}>
                  <td>{r.department || <span className="text-muted">(ingen afdeling)</span>}</td>
                  <td className="text-right tabular-nums">{r.total}</td>
                  <td className="text-right tabular-nums">{r.missing}</td>
                  <td><button className="text-xs text-primary hover:underline" onClick={() => onShow({ departments: [r.department], status: ['pending'] })}>Vis i tabellen</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.byZone.length > 0 && (
          <div className="card p-0">
            <table className="table text-sm">
              <thead><tr><th>Zone</th><th>Postnr.</th><th className="text-right">Gaver</th><th className="text-right">Mangler</th><th></th></tr></thead>
              <tbody>
                {d.byZone.map((z) => (
                  <tr key={z.zoneId ?? 'outside'}>
                    <td>{z.name}</td>
                    <td className="text-xs">{z.zipFrom != null ? `${z.zipFrom}-${z.zipTo}` : ''}</td>
                    <td className="text-right tabular-nums">{z.total}</td>
                    <td className="text-right tabular-nums">{z.missing}</td>
                    <td>{z.zipFrom != null && <button className="text-xs text-primary hover:underline" onClick={() => onShow({ zipFrom: String(z.zipFrom), zipTo: String(z.zipTo), status: ['pending'] })}>Vis i tabellen</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, render }: { label: string; options: string[]; value: string[]; onChange: (v: string[]) => void; render?: (v: string) => string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" className="input w-44 truncate text-left" onClick={() => setOpen(!open)}>{value.length ? `${label}: ${value.length}` : label}</button>
      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-md border border-line bg-white p-2 shadow-lg" onMouseLeave={() => setOpen(false)}>
          {options.map((o) => <label key={o} className="flex items-center gap-2 py-0.5 text-sm"><input type="checkbox" checked={value.includes(o)} onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} /> {render ? render(o) : o || '(ingen)'}</label>)}
          {value.length > 0 && <button className="mt-1 text-xs text-primary" onClick={() => onChange([])}>Ryd</button>}
        </div>
      )}
    </div>
  );
}

function OrdersTab({ shopId, filter, setFilter, zones, onError }: { shopId: string; filter: OrderFilter; setFilter: (f: OrderFilter) => void; zones: HandoutZoneDto[]; onError: (e: unknown) => void }) {
  const [limit, setLimit] = useState(200);
  const [open, setOpen] = useState<string | null>(null);
  const params = filterParams(shopId, filter);
  params.set('perPage', String(limit));
  const list = useQuery({ queryKey: ['handout-orders', params.toString()], queryFn: () => get<HandoutOrderListDto>(`/handout/orders?${params}`) });
  const set = (p: Partial<OrderFilter>) => setFilter({ ...filter, ...p });
  const exportUrl = (format: 'xlsx' | 'csv') => `/api/handout/export?${filterParams(shopId, filter)}&format=${format}`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelect label="Status" options={[...HANDOUT_STATUSES]} value={filter.status} onChange={(status) => set({ status: status as HandoutStatus[] })} render={(s) => HANDOUT_STATUS_LABELS[s as HandoutStatus]} />
        <input className="input w-24" placeholder="Postnr. fra" value={filter.zipFrom} onChange={(e) => set({ zipFrom: e.target.value })} />
        <input className="input w-24" placeholder="til" value={filter.zipTo} onChange={(e) => set({ zipTo: e.target.value })} />
        {zones.length > 0 && (
          <select className="input w-40" value="" onChange={(e) => { const z = zones.find((x) => x.id === e.target.value); if (z) set({ zipFrom: String(z.zipFrom), zipTo: String(z.zipTo) }); }}>
            <option value="">Zone…</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
        )}
        <MultiSelect label="Afdeling" options={list.data?.departments ?? []} value={filter.departments ?? []} onChange={(d) => set({ departments: d.length ? d : null })} />
        <MultiSelect label="Gave" options={list.data?.products ?? []} value={filter.products} onChange={(products) => set({ products })} />
        <input className="input w-56" placeholder="Søg navn, e-mail, ordrenr." value={filter.q} onChange={(e) => set({ q: e.target.value })} />
        <button className="text-sm text-primary hover:underline" onClick={() => setFilter(emptyFilter)}>Nulstil</button>
        <span className="ml-auto flex gap-2">
          <a className="btn" href={exportUrl('xlsx')}>Eksportér XLSX ({list.data?.total ?? 0})</a>
          <a className="btn" href={exportUrl('csv')}>CSV</a>
        </span>
      </div>
      {list.data && list.data.missingZip > 0 && <p className="text-xs text-amber-800">{list.data.missingZip} ordrer har intet postnummer (eller et der ikke starter med cifre) og falder uden for postnummer-filtre.</p>}
      {list.isLoading ? <Spinner /> : (
        <div className="card p-0">
          <table className="table text-sm">
            <thead><tr><th>Ordre</th><th>Navn</th><th>Postnr.</th><th>Afdeling</th><th>Gave</th><th>Status</th><th>Udleveret</th></tr></thead>
            <tbody>
              {list.data?.items.map((o) => (
                <tr key={o.id} className="cursor-pointer" onClick={() => setOpen(o.id)}>
                  <td className="tabular-nums">{o.number}</td>
                  <td>{o.name}<div className="text-xs text-muted">{o.username ?? o.email ?? ''}</div></td>
                  <td>{o.zip}{o.zone && <div className="text-xs text-muted">{o.zone}</div>}</td>
                  <td>{o.department}</td>
                  <td className="max-w-64 truncate">{o.products.map((p) => p.title).join(', ')}</td>
                  <td><Badge tone={statusTone[o.status]}>{HANDOUT_STATUS_LABELS[o.status]}</Badge></td>
                  <td className="text-xs">{o.handedOutAt ? `${fmt(o.handedOutAt)} · ${o.handedOutByName ?? ''}` : ''}</td>
                </tr>
              ))}
              {list.data?.items.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen ordrer</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {list.data && list.data.total > list.data.items.length && <button className="btn" onClick={() => setLimit(limit + 200)}>Vis 200 flere ({list.data.total - list.data.items.length} tilbage)</button>}
      {open && <OrderDrawer id={open} onClose={() => setOpen(null)} onError={onError} />}
    </div>
  );
}

function OrderDrawer({ id, onClose, onError }: { id: string; onClose: () => void; onError: (e: unknown) => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ['handout-drawer', id], queryFn: () => get<HandoutDrawerDto>(`/handout/orders/${id}`) });
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (kind: 'handout' | 'recall') => post<HandoutResultDto>(`/handout/orders/${id}/${kind}`, { note: note || undefined, app: 'editor' }),
    onSuccess: (r) => { setMsg(r.message); setNote(''); void qc.invalidateQueries({ queryKey: ['handout-drawer', id] }); void qc.invalidateQueries({ queryKey: ['handout-orders'] }); void qc.invalidateQueries({ queryKey: ['handout-overview'] }); },
    onError,
  });
  const o = d.data?.order;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Ordre {o?.number}</h2><button onClick={onClose}><X size={20} /></button></div>
        {!o ? <Spinner /> : (
          <div className="space-y-4 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Badge tone={statusTone[o.status]}>{HANDOUT_STATUS_LABELS[o.status]}</Badge>
                <div className="mt-2 text-base font-semibold">{o.name}</div>
                <div className="text-muted">{[o.email, o.username !== o.email ? o.username : null].filter(Boolean).join(' · ')}</div>
                <div className="text-muted">{[o.zip, o.zone, o.department].filter(Boolean).join(' · ')}</div>
              </div>
              {d.data?.qrSvg && <div className="h-28 w-28 shrink-0" dangerouslySetInnerHTML={{ __html: d.data.qrSvg }} />}
            </div>
            <div className="space-y-1">{o.products.map((p, i) => <div key={i} className="flex items-center gap-2">{p.image && <img src={p.image} alt="" className="h-10 w-10 rounded object-contain" />}{p.quantity > 1 ? `${p.quantity} × ` : ''}{p.title}</div>)}</div>
            {o.handedOutAt && <div className="rounded bg-green-50 p-2 text-green-800">Udleveret {fmt(o.handedOutAt)} af {o.handedOutByName}{o.note ? ` · "${o.note}"` : ''}{o.recallUntil ? ` · kan tages tilbage til ${new Date(o.recallUntil).toLocaleDateString('da-DK')}` : ''}</div>}
            {msg && <div className="rounded bg-canvas p-2">{msg}</div>}
            {(o.status === 'pending' || (o.status === 'handed_out' && o.canRecall && can('handout.recall'))) && (
              <div className="space-y-2">
                <input className="input" placeholder="Note (valgfri)" value={note} onChange={(e) => setNote(e.target.value)} />
                {o.status === 'pending' && <button className="btn btn-primary" disabled={act.isPending} onClick={() => act.mutate('handout')}>Udlevér herfra</button>}
                {o.status === 'handed_out' && <button className="btn btn-danger" disabled={act.isPending} onClick={() => confirm('Tag gaven tilbage?') && act.mutate('recall')}>Tag tilbage</button>}
              </div>
            )}
            {o.token && <button className="text-xs text-primary hover:underline" onClick={() => void navigator.clipboard.writeText(o.token!)}>Kopiér token</button>}
            <div>
              <div className="label">Historik</div>
              {d.data!.history.length === 0 && <div className="text-muted">Ingen hændelser</div>}
              {d.data!.history.map((e) => <div key={e.id} className="border-b border-line py-1"><span className="text-muted">{fmt(e.at)}</span> · {actionLabel[e.action] ?? e.action} · {e.userName}{e.note ? ` · "${e.note}"` : ''}{e.app === 'editor' ? ' (editor)' : ''}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LogTab({ shopId }: { shopId: string }) {
  const [from, setFrom] = useState(new Date(Date.now() - 6 * 86400_000).toLocaleDateString('sv-SE'));
  const [to, setTo] = useState(new Date().toLocaleDateString('sv-SE'));
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const p = new URLSearchParams({ shopId, page: String(page), perPage: '100' });
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  if (action) p.set('action', action);
  if (q.trim()) p.set('q', q.trim());
  const log = useQuery({ queryKey: ['handout-log', p.toString()], queryFn: () => get<{ items: HandoutEventDto[]; total: number }>(`/handout/log?${p}`) });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input className="input w-40" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        <input className="input w-40" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        <select className="input w-48" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}><option value="">Alle hændelser</option>{Object.entries(actionLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <input className="input w-56" placeholder="Navn, note, bruger, ordrenr." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      </div>
      <div className="card p-0">
        <table className="table text-sm">
          <thead><tr><th>Tid</th><th>Hændelse</th><th>Ordre</th><th>Navn</th><th>Bruger</th><th>Note</th></tr></thead>
          <tbody>
            {log.data?.items.map((e) => <tr key={e.id}><td className="whitespace-nowrap text-xs">{fmt(e.at)}</td><td>{actionLabel[e.action] ?? e.action}</td><td className="tabular-nums">{e.orderNumber}</td><td>{e.name}</td><td>{e.userName}{e.app === 'editor' ? <span className="text-xs text-muted"> (editor)</span> : null}</td><td className="text-xs">{e.note}</td></tr>)}
            {log.data?.items.length === 0 && <tr><td colSpan={6} className="text-muted">Ingen hændelser</td></tr>}
          </tbody>
        </table>
      </div>
      {log.data && log.data.total > page * 100 && <button className="btn" onClick={() => setPage(page + 1)}>Næste side</button>}
    </div>
  );
}

function ReportsTab({ shopId, onError }: { shopId: string; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const presets = useQuery({ queryKey: ['handout-presets'], queryFn: () => get<HandoutPresetDto[]>('/handout/presets') });
  const zones = useQuery({ queryKey: ['handout-zones', shopId], queryFn: () => get<HandoutZoneDto[]>(`/handout/zones?shopId=${shopId}`) });
  const meta = useQuery({ queryKey: ['handout-orders', 'meta', shopId], queryFn: () => get<HandoutOrderListDto>(`/handout/orders?shopId=${shopId}&perPage=1`) });
  const [scope, setScope] = useState<'all' | 'zip' | 'department'>('all');
  const [zipFrom, setZipFrom] = useState('');
  const [zipTo, setZipTo] = useState('');
  const [zone, setZone] = useState('');
  const [departments, setDepartments] = useState<string[]>([]);
  const [status, setStatus] = useState<HandoutStatus[]>(['pending']);
  const [columns, setColumns] = useState<string[]>([...HANDOUT_COLUMNS]);
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [presetName, setPresetName] = useState('');
  const [shared, setShared] = useState(false);
  const params = useMemo(() => {
    const p = new URLSearchParams({ shopId, format, columns: columns.join(',') });
    if (status.length) p.set('status', status.join(','));
    if (scope === 'zip') { if (zipFrom) p.set('zipFrom', zipFrom); if (zipTo) p.set('zipTo', zipTo); if (zone) p.set('zone', zone); }
    if (scope === 'department' && departments.length) p.set('departments', departments.join('|'));
    return p;
  }, [shopId, format, columns, status, scope, zipFrom, zipTo, zone, departments]);
  const countParams = new URLSearchParams(params);
  countParams.delete('format');
  countParams.delete('columns');
  countParams.delete('zone');
  countParams.set('perPage', '1');
  const preview = useQuery({ queryKey: ['handout-orders', 'count', countParams.toString()], queryFn: () => get<HandoutOrderListDto>(`/handout/orders?${countParams}`) });
  const save = useMutation({
    mutationFn: () => post<HandoutPresetDto[]>('/handout/presets', { shopId, name: presetName, shared, format, columns, filters: { scope, zipFrom, zipTo, zone, departments, status } }),
    onSuccess: (d) => { qc.setQueryData(['handout-presets'], d); setPresetName(''); },
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => del<HandoutPresetDto[]>(`/handout/presets/${id}`), onSuccess: (d) => qc.setQueryData(['handout-presets'], d), onError });
  const load = (p: HandoutPresetDto) => {
    const f = p.filters as { scope?: 'all' | 'zip' | 'department'; zipFrom?: string; zipTo?: string; zone?: string; departments?: string[]; status?: HandoutStatus[] };
    setScope(f.scope ?? 'all'); setZipFrom(f.zipFrom ?? ''); setZipTo(f.zipTo ?? ''); setZone(f.zone ?? ''); setDepartments(f.departments ?? []); setStatus(f.status ?? []); setColumns(p.columns.length ? p.columns : [...HANDOUT_COLUMNS]); setFormat(p.format);
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
      <div className="card space-y-3 text-sm">
        <div className="flex flex-wrap gap-4">
          {(['all', 'zip', 'department'] as const).map((s) => <label key={s} className="flex items-center gap-1"><input type="radio" checked={scope === s} onChange={() => setScope(s)} /> {s === 'all' ? 'Alle' : s === 'zip' ? 'Postnummer-interval' : 'Afdelinger'}</label>)}
        </div>
        {scope === 'zip' && (
          <div className="flex flex-wrap items-center gap-2">
            {(zones.data?.length ?? 0) > 0 && <select className="input w-44" value={zone} onChange={(e) => { const z = zones.data!.find((x) => x.name === e.target.value); setZone(e.target.value); if (z) { setZipFrom(String(z.zipFrom)); setZipTo(String(z.zipTo)); } }}><option value="">Zone…</option>{zones.data!.map((z) => <option key={z.id}>{z.name}</option>)}</select>}
            <input className="input w-24" placeholder="Fra" value={zipFrom} onChange={(e) => { setZipFrom(e.target.value); setZone(''); }} />
            <input className="input w-24" placeholder="Til" value={zipTo} onChange={(e) => { setZipTo(e.target.value); setZone(''); }} />
          </div>
        )}
        {scope === 'department' && <MultiSelect label="Afdelinger" options={meta.data?.departments ?? []} value={departments} onChange={setDepartments} />}
        <div className="flex flex-wrap gap-3">{HANDOUT_STATUSES.map((s) => <label key={s} className="flex items-center gap-1"><input type="checkbox" checked={status.includes(s)} onChange={(e) => setStatus(e.target.checked ? [...status, s] : status.filter((x) => x !== s))} /> {HANDOUT_STATUS_LABELS[s]}</label>)}</div>
        <div>
          <div className="label">Kolonner</div>
          <div className="flex flex-wrap gap-3">{HANDOUT_COLUMNS.map((c) => <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={columns.includes(c)} onChange={(e) => setColumns(e.target.checked ? HANDOUT_COLUMNS.filter((x) => x === c || columns.includes(x)) : columns.filter((x) => x !== c))} /> {HANDOUT_COLUMN_LABELS[c]}</label>)}</div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1"><input type="radio" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} /> XLSX</label>
          <label className="flex items-center gap-1"><input type="radio" checked={format === 'csv'} onChange={() => setFormat('csv')} /> CSV (semikolon)</label>
          <span className="text-muted">{preview.data ? `${preview.data.total} rækker` : ''}</span>
          <a className="btn btn-primary ml-auto" href={`/api/handout/export?${params}`}>Hent rapport</a>
        </div>
        <div className="flex items-center gap-2 border-t border-line pt-3">
          <input className="input w-56" placeholder="Gem som forvalg…" value={presetName} onChange={(e) => setPresetName(e.target.value)} />
          <label className="flex items-center gap-1"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Del med alle editorer</label>
          <button className="btn" disabled={!presetName.trim() || save.isPending} onClick={() => save.mutate()}>Gem</button>
        </div>
      </div>
      <div className="card space-y-1 text-sm">
        <div className="label">Forvalg</div>
        {presets.data?.filter((p) => !p.shopId || p.shopId === shopId).map((p) => (
          <div key={p.id} className="flex items-center justify-between border-b border-line py-1">
            <button className="text-left text-primary hover:underline" onClick={() => load(p)}>{p.name}</button>
            <span className="flex items-center gap-2 text-xs text-muted">{p.shared && <Badge>delt</Badge>}{!p.mine && p.ownerName}{p.mine && <button onClick={() => remove.mutate(p.id)}><X size={14} /></button>}</span>
          </div>
        ))}
        {presets.data?.length === 0 && <div className="text-muted">Ingen forvalg endnu.</div>}
      </div>
    </div>
  );
}

function ZonesTab({ shopId, onError }: { shopId: string; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const zones = useQuery({ queryKey: ['handout-zones', shopId], queryFn: () => get<HandoutZoneDto[]>(`/handout/zones?shopId=${shopId}`) });
  const [form, setForm] = useState({ name: '', zipFrom: '', zipTo: '' });
  const done = (d: HandoutZoneDto[]) => { qc.setQueryData(['handout-zones', shopId], d); void qc.invalidateQueries({ queryKey: ['handout-context'] }); };
  const add = useMutation({ mutationFn: () => post<HandoutZoneDto[]>(`/handout/zones?shopId=${shopId}`, { name: form.name, zipFrom: Number(form.zipFrom), zipTo: Number(form.zipTo) }), onSuccess: (d) => { done(d); setForm({ name: '', zipFrom: '', zipTo: '' }); }, onError });
  const remove = useMutation({ mutationFn: (id: string) => del<HandoutZoneDto[]>(`/handout/zones/${id}?shopId=${shopId}`), onSuccess: done, onError });
  return (
    <div className="card max-w-2xl space-y-2 text-sm">
      <p className="text-muted">En zone er et navngivet postnummer-interval i shoppen. Den bruges som afdeling i overblikket, filtre, rapporter og brugeres område.</p>
      {zones.data?.map((z) => <div key={z.id} className="flex items-center justify-between border-b border-line py-1"><span className="font-medium">{z.name}</span><span className="text-muted">{z.zipFrom}-{z.zipTo} <button className="ml-2" onClick={() => remove.mutate(z.id)}><X size={14} /></button></span></div>)}
      <form className="flex gap-2 pt-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
        <input className="input" placeholder="Navn (fx Jylland)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className="input w-24" placeholder="Fra" value={form.zipFrom} onChange={(e) => setForm({ ...form, zipFrom: e.target.value })} />
        <input className="input w-24" placeholder="Til" value={form.zipTo} onChange={(e) => setForm({ ...form, zipTo: e.target.value })} />
        <button className="btn" disabled={!form.name || !form.zipFrom || !form.zipTo}>Tilføj</button>
      </form>
    </div>
  );
}

function scopeText(s: HandoutScope | null) {
  if (!s || s.kind === 'all') return 'Alle ordrer';
  if (s.kind === 'department') return `Afdelinger: ${s.departments.join(', ')}`;
  return `Postnr. ${s.from}-${s.to}${s.zone ? ` (${s.zone})` : ''}`;
}

function UsersTab({ shops, shopId, onError }: { shops: { id: string; name: string }[]; shopId: string; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['handout-users'], queryFn: () => get<HandoutUserDto[]>('/handout/users') });
  const [edit, setEdit] = useState<HandoutUserDto | null>(null);
  const [importing, setImporting] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <p className="text-sm text-muted">Brugere med rollen Udlevering. Roller tildeles under Brugere; her sættes shop og område.</p>
        <button className="btn btn-primary" onClick={() => setImporting(true)}>Importér e-mails</button>
      </div>
      <div className="card p-0">
        <table className="table text-sm">
          <thead><tr><th>Navn</th><th>E-mail</th><th>Roller</th><th>Shop</th><th>Område</th><th>Sidst inde</th></tr></thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id} className="cursor-pointer" onClick={() => setEdit(u)}>
                <td className="font-medium text-primary">{u.name}</td>
                <td>{u.email}</td>
                <td className="text-xs">{u.roles.join(', ')}</td>
                <td>{u.shopName ?? <span className="text-muted">alle</span>}</td>
                <td className="text-xs">{scopeText(u.scope)}</td>
                <td className="text-xs">{u.lastLoginAt ? fmt(u.lastLoginAt) : u.status === 'active' ? '–' : u.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <UserScopeModal user={edit} shops={shops} onClose={() => setEdit(null)} onSaved={(d) => { qc.setQueryData(['handout-users'], d); setEdit(null); }} onError={onError} />}
      {importing && <ImportUsersModal shops={shops} shopId={shopId} onClose={() => setImporting(false)} onDone={(d) => qc.setQueryData(['handout-users'], d)} onError={onError} />}
    </div>
  );
}

function ScopeEditor({ value, onChange, shopId }: { value: HandoutScope | null; onChange: (s: HandoutScope | null) => void; shopId: string | null }) {
  const meta = useQuery({ queryKey: ['handout-orders', 'meta', shopId], queryFn: () => get<HandoutOrderListDto>(`/handout/orders?shopId=${shopId}&perPage=1`), enabled: !!shopId });
  const zones = useQuery({ queryKey: ['handout-zones', shopId], queryFn: () => get<HandoutZoneDto[]>(`/handout/zones?shopId=${shopId}`), enabled: !!shopId });
  const kind = value?.kind ?? 'all';
  return (
    <div className="space-y-2">
      <div className="flex gap-4">
        {(['all', 'department', 'zip'] as const).map((k) => <label key={k} className="flex items-center gap-1"><input type="radio" checked={kind === k} onChange={() => onChange(k === 'all' ? null : k === 'department' ? { kind: 'department', departments: [] } : { kind: 'zip', from: 0, to: 9999, zone: null })} /> {k === 'all' ? 'Alle ordrer' : k === 'department' ? 'Afdelinger' : 'Postnummer-interval'}</label>)}
      </div>
      {value?.kind === 'department' && (
        <div className="max-h-48 overflow-y-auto rounded border border-line p-2">
          {(meta.data?.departments ?? []).map((d) => <label key={d} className="flex items-center gap-2"><input type="checkbox" checked={value.departments.includes(d)} onChange={(e) => onChange({ kind: 'department', departments: e.target.checked ? [...value.departments, d] : value.departments.filter((x) => x !== d) })} /> {d || '(ingen afdeling)'}</label>)}
        </div>
      )}
      {value?.kind === 'zip' && (
        <div className="flex flex-wrap items-center gap-2">
          {(zones.data?.length ?? 0) > 0 && <select className="input w-40" value={value.zone ?? ''} onChange={(e) => { const z = zones.data!.find((x) => x.name === e.target.value); if (z) onChange({ kind: 'zip', from: z.zipFrom, to: z.zipTo, zone: z.name }); }}><option value="">Zone…</option>{zones.data!.map((z) => <option key={z.id}>{z.name}</option>)}</select>}
          <input className="input w-24" value={value.from} onChange={(e) => onChange({ ...value, from: Number(e.target.value) || 0, zone: null })} />
          <input className="input w-24" value={value.to} onChange={(e) => onChange({ ...value, to: Number(e.target.value) || 0, zone: null })} />
        </div>
      )}
    </div>
  );
}

function UserScopeModal({ user, shops, onClose, onSaved, onError }: { user: HandoutUserDto; shops: { id: string; name: string }[]; onClose: () => void; onSaved: (d: HandoutUserDto[]) => void; onError: (e: unknown) => void }) {
  const [shopId, setShopId] = useState(user.shopId ?? '');
  const [scope, setScope] = useState<HandoutScope | null>(user.scope);
  const save = useMutation({ mutationFn: () => put<HandoutUserDto[]>(`/handout/users/${user.id}`, { shopId: shopId || null, scope: scope && (scope.kind !== 'department' || scope.departments.length) ? scope : null }), onSuccess: onSaved, onError });
  return (
    <Modal title={user.name} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <label className="block"><span className="label">Shop</span><select className="input" value={shopId} onChange={(e) => setShopId(e.target.value)}><option value="">Alle shops (editor)</option>{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
        <div><div className="label">Område</div><ScopeEditor value={scope} onChange={setScope} shopId={shopId || null} /></div>
        <div className="flex justify-end"><button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Gem</button></div>
      </div>
    </Modal>
  );
}

function ImportUsersModal({ shops, shopId, onClose, onDone, onError }: { shops: { id: string; name: string }[]; shopId: string; onClose: () => void; onDone: (d: HandoutUserDto[]) => void; onError: (e: unknown) => void }) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<'handout_scanner' | 'handout_editor'>('handout_scanner');
  const [shop, setShop] = useState(shopId);
  const [scope, setScope] = useState<HandoutScope | null>(null);
  const [res, setRes] = useState<{ created: string[]; existing: string[]; invalid: string[] } | null>(null);
  const run = useMutation({
    mutationFn: () => post<{ created: string[]; existing: string[]; invalid: string[]; users: HandoutUserDto[] }>('/handout/users/import', { emails, role, shopId: shop, scope }),
    onSuccess: (r) => { setRes(r); onDone(r.users); },
    onError,
  });
  return (
    <Modal title="Importér udleveringsbrugere" onClose={onClose}>
      <div className="space-y-3 text-sm">
        {res ? (
          <div className="space-y-1">
            <div>Oprettet og inviteret: {res.created.length}</div>
            {res.existing.length > 0 && <div className="text-muted">Findes allerede: {res.existing.join(', ')}</div>}
            {res.invalid.length > 0 && <div className="text-red-700">Ugyldige: {res.invalid.join(', ')}</div>}
            <div className="flex justify-end"><button className="btn" onClick={onClose}>Luk</button></div>
          </div>
        ) : (
          <>
            <textarea className="input font-mono text-xs" rows={8} placeholder="En e-mail pr. linje (højst 500)" value={emails} onChange={(e) => setEmails(e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="label">Rolle</span><select className="input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}><option value="handout_scanner">Scanner</option><option value="handout_editor">Editor</option></select></label>
              <label className="block"><span className="label">Shop</span><select className="input" value={shop} onChange={(e) => setShop(e.target.value)}>{shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
            </div>
            <div><div className="label">Område</div><ScopeEditor value={scope} onChange={setScope} shopId={shop} /></div>
            <p className="text-xs text-muted">Hver ny bruger får en mail med et link til at vælge adgangskode og logger derefter ind på /m.</p>
            <div className="flex justify-end"><button className="btn btn-primary" disabled={!emails.trim() || run.isPending} onClick={() => run.mutate()}>Importér</button></div>
          </>
        )}
      </div>
    </Modal>
  );
}
